/* Hero animation controls: pause, step, and drag to spin.
 *
 * The hero animation is a 360° turntable render, not a film. What a viewer wants
 * from it is to stop it on the face they care about and turn it by hand — which
 * `controls` on a <video> serves badly: it offers a scrubber, a timeline and a
 * volume slider for a silent, looping, 21-second product spin.
 *
 * So: a pause button and a dragger. Time is the rotation angle, and that is the whole
 * model — the dragger's value is a position in the turn, and dragging the render maps
 * horizontal travel onto currentTime, a full width being one full rotation.
 *
 * Playback is NOT started here. The element carries `autoplay`, so a failure to load
 * this file costs the controls and not the animation.
 */
(() => {
  const SCRUB_MAX = 1000; // thousandths of a turn: finer than a pixel at any size

  const wrap = (time, duration) => ((time % duration) + duration) % duration;

  const setup = (root) => {
    const video = root.querySelector('video');
    const bar = root.querySelector('[data-hero-video-controls]');
    if (!video || !bar) return;

    const toggle = bar.querySelector('[data-hero-video-toggle]');
    const scrub = bar.querySelector('[data-hero-video-scrub]');

    // A duration is the one thing every control here depends on: seeking without it
    // silently does nothing, so the bar stays hidden until the video reports one.
    const ready = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      bar.classList.add('is-ready');
      root.classList.add('is-ready');
      syncState();
    };

    const syncState = () => {
      const paused = video.paused;
      root.classList.toggle('is-paused', paused);
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(paused));
      // The label names the ACTION, which is the opposite of the current state.
      const label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
      if (label) {
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('title', label);
      }
    };

    const seekBy = (seconds) => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      video.currentTime = wrap(video.currentTime + seconds, video.duration);
    };

    // The still beneath is held back by CSS until the animation has a frame to show,
    // so that the hero does not flash a different booth on the way in. Cancelling the
    // reveal is all this does — if it never runs, the CSS reveals the still anyway.
    const shot = root.closest('.shot');
    const markPainted = () => shot && shot.classList.add('hero-video-ok');
    if (video.readyState >= 2) markPainted();
    video.addEventListener('loadeddata', markPainted);
    video.addEventListener('playing', markPainted);

    if (video.readyState >= 1) ready();
    video.addEventListener('loadedmetadata', ready);
    video.addEventListener('durationchange', ready);
    video.addEventListener('play', syncState);
    video.addEventListener('pause', syncState);

    if (toggle) {
      toggle.dataset.labelPause = toggle.getAttribute('aria-label') || '';
      toggle.dataset.labelPlay = toggle.dataset.labelPlay || toggle.dataset.labelPause;
      toggle.addEventListener('click', () => {
        if (video.paused) {
          const played = video.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    if (scrub) {
      // A drag on the scrubber is a manual act, so it takes ownership: it pauses first,
      // otherwise playback keeps moving the thumb out from under the pointer.
      scrub.addEventListener('input', () => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;
        if (!video.paused) video.pause();
        video.currentTime = (Number(scrub.value) / SCRUB_MAX) * video.duration;
      });
      // While it plays the thumb has to follow the turn, or the control lies about where
      // in the rotation you are. Skipped whenever the scrubber has focus, so a keyboard
      // user's own position is never overwritten mid-press.
      video.addEventListener('timeupdate', () => {
        if (document.activeElement === scrub) return;
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;
        scrub.value = String(Math.round((video.currentTime / video.duration) * SCRUB_MAX));
      });
    }

    // Drag to spin. One full width of travel is one full rotation, so the gesture
    // feels the same on a phone and on a 27-inch monitor.
    let dragId = null;
    let dragX = 0;
    let wasPlaying = false;

    video.addEventListener('pointerdown', (event) => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      dragId = event.pointerId;
      dragX = event.clientX;
      wasPlaying = !video.paused;
      if (wasPlaying) video.pause();
      root.classList.add('is-dragging');
      video.setPointerCapture(dragId);
    });

    video.addEventListener('pointermove', (event) => {
      if (dragId !== event.pointerId || !root.classList.contains('is-dragging')) return;
      const width = video.getBoundingClientRect().width || 1;
      const delta = event.clientX - dragX;
      dragX = event.clientX;
      seekBy((delta / width) * video.duration);
      // The dragger is the readout of where in the turn we are, so spinning by hand has
      // to move it as well — two controls over one rotation must never disagree.
      if (scrub) {
        scrub.value = String(Math.round((video.currentTime / video.duration) * SCRUB_MAX));
      }
    });

    const endDrag = (event) => {
      if (dragId !== event.pointerId) return;
      root.classList.remove('is-dragging');
      if (video.hasPointerCapture(dragId)) video.releasePointerCapture(dragId);
      dragId = null;
      // A drag is a deliberate stop on a chosen face. Resuming would undo it, so the
      // video stays where it was left and the play button says so.
      if (wasPlaying) syncState();
    };
    video.addEventListener('pointerup', endDrag);
    video.addEventListener('pointercancel', endDrag);
  };

  const init = () => document.querySelectorAll('[data-hero-video]').forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
